import { createContext, PropsWithChildren, useCallback, useContext, useState } from 'react';

import { HeaderTheme, HeaderSize, HeaderThemeType, HeaderSizeType } from '../constants/header';
import { LocalNavVerticalItem, VerticalItem } from '../constants/lnb';

export type LayoutHeaderContextType = {
  isFlightMode: boolean;
  isDisabled: boolean;
  theme: HeaderThemeType;
  size: HeaderSizeType;
  user: {
    id: number;
    hashMemberId: string;
    username: string;
    email: string;
    phoneNumber: string;
    point: number;
    numberOfCoupons: number;
    profileImage: string;
  } | null;
  lnbVerticalItems: LocalNavVerticalItem[];
  mainVerticalItems: VerticalItem[] | null;
  subVerticalItems: VerticalItem[] | null;
  isMainPage?: boolean;
  excludeLnb?: boolean;
  pathname?: string;
  setTheme: (nextTheme: HeaderThemeType) => void;
  setSize: (nextSize: HeaderSizeType) => void;
  setDisabled: (nextDisabled: boolean) => void;
  setExcludeLnb: (nextExcludeLnb: boolean) => void;
};

const defaultState = {
  user: null,
  isDisabled: false,
  lnbVerticalItems: [],
  mainVerticalItems: [],
  subVerticalItems: [],
  theme: HeaderTheme.white,
  size: HeaderSize.default,
  isFlightMode: false,
  excludeLnb: false,
  isMainPage: false,
  setTheme: () => null,
  setSize: () => null,
  setDisabled: () => null,
  setExcludeLnb: () => null,
};

export const LayoutHeaderContext = createContext<LayoutHeaderContextType>(defaultState);

export function LayoutHeaderContextProvider({
  initialState,
  children,
}: PropsWithChildren<{
  initialState: Partial<LayoutHeaderContextType>;
}>) {
  const combineState = { ...defaultState, ...initialState };
  const [currentTheme, setCurrentTheme] = useState<HeaderThemeType>(combineState.theme);
  const [currentSize, setCurrentSize] = useState<HeaderSizeType>(combineState.size);
  const [isExcludeLnb, setExcludeLnb] = useState<boolean>(combineState.excludeLnb ?? false);
  const [currentDisabled, setCurrentDisabled] = useState<boolean>(combineState.isDisabled ?? false);

  const handleTheme = useCallback((nextTheme: HeaderThemeType) => setCurrentTheme(nextTheme), []);
  const handleSize = useCallback((nextSize: HeaderSizeType) => setCurrentSize(nextSize), []);
  const handleDisabled = useCallback(
    (nextDisabled: boolean) => setCurrentDisabled(nextDisabled),
    []
  );
  const handleExcludeLnb = useCallback(
    (nextExcludeLnb: boolean) => setExcludeLnb(nextExcludeLnb),
    []
  );

  return (
    <LayoutHeaderContext.Provider
      value={{
        ...combineState,
        theme: currentTheme,
        size: currentSize,
        isDisabled: currentDisabled,
        excludeLnb: isExcludeLnb,
        setTheme: handleTheme,
        setSize: handleSize,
        setDisabled: handleDisabled,
        setExcludeLnb: handleExcludeLnb,
      }}
    >
      {children}
    </LayoutHeaderContext.Provider>
  );
}

export function useLayoutHeaderContext() {
  return useContext(LayoutHeaderContext);
}
